import { UseFormReturn } from "react-hook-form";
import EditIcon from "../../icons/edit.icon";
import { LogIcon } from "../../icons/logIcon";
import { Product, ProductFormData } from "../../types/product";
import { Tag } from "../reuseables/list.comp";
import { useTranslation } from "../../core/translation.core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { MembershipListPrices, PriceInput, RetailListPrices } from "./productListPrices.comp";
import { RetailShopRes } from "../../types/retailShop";
import { Membership } from "../../types/membership";
import productCore, { updateProductPrice } from "../../core/product.core";
import localeCore from "../../core/locale.core";
import { Category } from "../../types/category";
import inventoryCore from "../../core/inventory.core";
import Link from "next/link";
import Image from "next/image";
import { ValueUpdateInput } from "../reuseables/valueupdate.comp";
import useProductBasicInfo from "../../hooks/useProductBasicInfo.hook";

export function Cats({ cats }: { cats: Category[] }) {
  if (!cats || cats.length === 0) return <div className="flex flex-col justify-start ">No Category</div>;
  return (
    <div className="flex flex-col justify-start ">
      {cats.map((c) => (
        <span key={c.code}>{localeCore.getLocaleValueDefaultFallback(c.name)}</span>
      ))}
    </div>
  );
}

export function PriceSection({
  product,
  retailShops,
  methods,
  memberships,
}: {
  product: Product;
  retailShops?: RetailShopRes[];
  methods: UseFormReturn<ProductFormData, any>;
  memberships?: Membership[];
}) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  const handleSubmit = async (fieldName: string, price: number) => {
    const res = await updateProductPrice({ productId: product.id, [fieldName]: price });

    if (res.status !== 200) {
      toast.error(t(msg.errors.contactAdmin));
      return false;
    }

    queryClient.invalidateQueries({ queryKey: ["productSingle", product.id] });
    if (product.parentId) {
      queryClient.invalidateQueries({ queryKey: ["productSingle", product.parentId] });
    }
    return true;
  };

  return (
    <>
      <div>
        {product.productType !== "variationParent" && (
          <>
            <Tag>{t(msg.product.price)}</Tag>
            <PriceInput
              fieldName="price"
              methods={methods}
              currentValue={product.price}
              handleSubmit={(price) => handleSubmit("price", price)}
            />
          </>
        )}
      </div>
      <div>
        {product.productType !== "variationParent" && (
          <>
            <Tag>{t(msg.product.eshopPrice)}</Tag>
            <PriceInput
              fieldName="eshopPrice"
              methods={methods}
              currentValue={product.eshopPrice}
              handleSubmit={(price) => handleSubmit("eshopPrice", price)}
            />
          </>
        )}
      </div>
      <div>
        {product.productType !== "variationParent" && (
          <>
            <Tag>{t(msg.product.discountPrice)}</Tag>
            <PriceInput
              fieldName="discountPrice"
              methods={methods}
              currentValue={product.discountPrice ?? 0}
              handleSubmit={(price) => handleSubmit("discountPrice", price)}
            />
          </>
        )}
      </div>

      {retailShops && retailShops.length > 0 ? (
        <div>
          {product.productType !== "variationParent" && (
            <>
              <Tag>{t(msg.product.retailPrices)}</Tag>
              <div className="space-y-2">
                <PriceInput
                  fieldName="defaultRetailPrice"
                  methods={methods}
                  placeholder={t(msg.product.default)}
                  currentValue={product.defaultRetailPrice}
                  handleSubmit={(price) => handleSubmit("defaultRetailPrice", price)}
                />
                <RetailListPrices
                  product={product}
                  methods={methods}
                  retailShops={retailShops}
                  currentRetailPrices={product.retailPrices}
                />
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div> {product.productType !== "variationParent" && <>{"Not Listed"}</>}</div>
        </>
      )}

      {memberships && memberships?.length > 0 ? (
        <div>
          {product.productType !== "variationParent" && (
            <>
              <Tag>{t(msg.product.membershipPrices)}</Tag>
              <MembershipListPrices
                product={product}
                methods={methods}
                memberships={memberships}
                currentMembershipPrices={product.membershipPrices}
              />
            </>
          )}
        </div>
      ) : (
        <>
          <div> {product.productType !== "variationParent" && <>{"Not Listed"}</>}</div>
        </>
      )}
    </>
  );
}

export function InventorySection({
  product,
  methods,
  isVisible,
  warehouseId,
}: {
  product: Product;
  methods: UseFormReturn<ProductFormData, any>;
  isVisible: boolean;
  warehouseId?: string;
}) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  const {
    data: productInventory,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["singleProductInventory", product.id],
    queryFn: () => productCore.getSingleProductInventory(product.id),
    enabled: isVisible && product.productType !== "bundle",
  });

  const hasBoxConversion = !!product.boxConversion;

  const { data: targetProductInfo } = useProductBasicInfo(product.boxConversion?.productId);

  if (product.productType === "bundle") {
    return <div>{t(msg.inventory.bundleInventoryNotice)}</div>;
  }

  const onSubmit = async (productId: string, warehouseId: string, data: any) => {
    const res = await inventoryCore.updateSingleInventoryCount(productId, warehouseId, data);
    if (res.message !== "success") {
      toast.error(t(msg.errors.contactAdmin));
      return false;
    }

    queryClient.invalidateQueries({ queryKey: ["productSingle", product.id] });
    if (product.parentId) {
      queryClient.invalidateQueries({ queryKey: ["productSingle", product.parentId] });
    }
    return true;
  };

  if (!isVisible) {
    if (hasBoxConversion) {
      return (
        <div className="dark:text-white mb-2">
          {targetProductInfo ? (
            <div className="text-sm">
              <div className="font-medium mb-1">{t(msg.product.boxConversion)}</div>
              <div>
                {t(msg.product.sku)}: {targetProductInfo.sku} | {t(msg.product.conversionRate)}:{" "}
                {product.boxConversion?.conversionRate}
              </div>
            </div>
          ) : (
            <div></div>
          )}
        </div>
      );
    }
    return <div></div>;
  }
  if (error) return <div>{"Cant get inventory please try again"}</div>;
  if (isLoading) return <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-her-robinEggBlue mx-2"></span>;

  const warehouseInventory = warehouseId
    ? productInventory?.inventories?.find((inv) => inv.warehouseId === warehouseId)
    : undefined;

  const warehouseIndex = warehouseId
    ? productInventory?.inventories?.findIndex((inv) => inv.warehouseId === warehouseId)
    : -1;

  if (warehouseId) {
    if (productInventory && warehouseInventory) {
      return (
        <div className="flex flex-row items-center gap-2">
          {hasBoxConversion ? (
            <div className="text-sm text-gray-600 dark:text-gray-400">{t(msg.inventory.boxConversionInventoryNotice)}</div>
          ) : (
            <>
              <ValueUpdateInput
                fieldName={`warehouse.${warehouseIndex}.count`}
                methods={methods}
                currentValue={warehouseInventory.count}
                handleSubmit={(price) => onSubmit(product.id, warehouseId, price)}
                placeholder="Count"
              />
              <Link
                className="underline"
                href={`/inventory/logs?sku=${product.sku}&warehouseId=${warehouseId}`}
              >
                <LogIcon />
              </Link>
            </>
          )}
        </div>
      );
    }

    if (product.productType === "variationParent") {
      return <div className="text-sm text-gray-500 dark:text-gray-400">{t(msg.inventory.parentInventoryNotice)}</div>;
    }

    if (hasBoxConversion && targetProductInfo) {
      return (
        <div className="text-sm dark:text-white">
          <div className="font-medium mb-1">{t(msg.product.boxConversion)}</div>
          <div>
            {t(msg.product.sku)}: {targetProductInfo.sku} | {t(msg.product.conversionRate)}:{" "}
            {product.boxConversion?.conversionRate}
          </div>
        </div>
      );
    }

    return <div className="text-sm text-gray-500 dark:text-gray-400">-</div>;
  }

  return (
    <div>
      {productInventory && productInventory?.inventories?.length > 0 ? (
        <>
          <Tag>{t(msg.inventory.inventory)}</Tag>
          {hasBoxConversion && (
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t(msg.inventory.boxConversionInventoryNotice)}</div>
          )}
          {!hasBoxConversion && (
            <div className="flex flex-row justify-between">
              <div className="flex flex-wrap gap-4 flex-col">
                {productInventory?.inventories?.map((warehouse, i) => (
                  <div key={i} className="flex flex-row  justify-between gap-2">
                    <p className="">{warehouse.warehouseName}</p>
                    <div className="flex flex-row items-center gap-4">
                      <ValueUpdateInput
                        key={i}
                        fieldName={`warehouse.${i}.count`}
                        methods={methods}
                        currentValue={warehouse.count}
                        handleSubmit={(price) => onSubmit(product.id, warehouse.warehouseId, price)}
                        placeholder="Count"
                      />
                      <Link
                        key={Math.random()}
                        className="underline"
                        href={`/inventory/logs?sku=${product.sku}&warehouseId=${warehouse.warehouseId}`}
                      >
                        <LogIcon />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              <Link href={`/inventory/edit/${product.id}`}>
                {productInventory.inventories?.length > 0 && <EditIcon className="dark:text-white w-6" />}
              </Link>
            </div>
          )}
        </>
      ) : (
        <>
          {product.productType === "variationParent" && <div>{t(msg.inventory.parentInventoryNotice)}</div>}
          {hasBoxConversion && targetProductInfo && (
            <div className="text-sm dark:text-white">
              <div className="font-medium mb-1">{t(msg.product.boxConversion)}</div>
              <div>
                {t(msg.product.sku)}: {targetProductInfo.sku} | {t(msg.product.conversionRate)}:{" "}
                {product.boxConversion?.conversionRate}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
export function ImageSectiion({ product }: { product: Product }) {
  const { t, msg } = useTranslation();

  if (!product.featureMedia) return <div></div>;

  return (
    <div className={`row-span-5 lg:row-span-2 ${product.status !== "active" ? "opacity-25" : ""}`}>
      {product.featureMedia ? (
        <Image
          unoptimized
          src={product.featureMedia.thumbnailUrl}
          width={50}
          height={50}
          alt={localeCore.getLocaleValueDefault(product.name) ?? ""}
        />
      ) : null}
      <div> {product.status !== "active" ? t(msg.general.disabled) : ""}</div>
    </div>
  );
}

export function CostSection({ product, methods }: { product: Product; methods: UseFormReturn<ProductFormData, any> }) {
  const { t, msg } = useTranslation();

  const queryClient = useQueryClient();

  const onSubmit = async (productId: string, cost: number) => {
    const res = await productCore.updateProductCost(productId, cost);
    if (res.message !== "success") {
      toast.error(t(msg.errors.contactAdmin));
      return false;
    }

    queryClient.invalidateQueries({ queryKey: ["productSingle", product.id] });
    if (product.parentId) {
      queryClient.invalidateQueries({ queryKey: ["productSingle", product.parentId] });
    }
    return true;
  };

  return (
    <>
      <Tag>{t(msg.product.cost)}</Tag>
      <div>
        <ValueUpdateInput
          fieldName={`cost`}
          methods={methods}
          currentValue={product.cost || 0}
          handleSubmit={(cost) => onSubmit(product.id, cost)}
          placeholder="Cost"
        />
      </div>
    </>
  );
}
